import React from "react";

import { useQuery } from "@tanstack/react-query";
import {
  CheckIcon,
  CircleDashedIcon,
  FileJson,
  Loader2,
  XIcon,
} from "lucide-react";

import { CopyButton } from "@/components/common/copy-button";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { useItemsRepository } from "@/pages/tina/items/hooks/use-items-repository";
import { formatUsdCost } from "@/pages/tina/lib/format-cost";
import type {
  StageArtifactRead,
  StageArtifactsRead,
  StageExecutionRead,
  StageExecutionStatus,
} from "@/types/tina";

const ICON: Record<StageExecutionStatus, React.ReactNode> = {
  ok: <CheckIcon className="h-4 w-4 text-emerald-600" />,
  inherited: <CheckIcon className="h-4 w-4 text-emerald-400" />,
  failed_retryable: <CircleDashedIcon className="h-4 w-4 text-amber-600" />,
  failed: <XIcon className="h-4 w-4 text-red-600" />,
  skipped: <CircleDashedIcon className="h-4 w-4 text-zinc-500" />,
  hitl: <CircleDashedIcon className="h-4 w-4 text-orange-600" />,
  duplicate: <CircleDashedIcon className="h-4 w-4 text-zinc-500" />,
};

export function StageTimeline({
  executions,
}: {
  executions: StageExecutionRead[];
}) {
  const [selectedExecution, setSelectedExecution] =
    React.useState<StageExecutionRead | null>(null);

  if (executions.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">No stage executions yet.</p>
    );
  }
  return (
    <>
      <ol className="border-l border-border pl-4 space-y-2">
        {executions.map((execution) => (
          <li key={execution.id} className="text-sm">
            <button
              type="button"
              onClick={() => setSelectedExecution(execution)}
              className="group -ml-1 flex w-[calc(100%+0.25rem)] flex-col rounded-md px-1 py-1.5 text-left transition-colors hover:bg-accent/60 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <span className="flex items-center gap-2">
                {ICON[execution.status]}
                <span className="font-medium group-hover:text-foreground">
                  {execution.stage}
                </span>
                <span className="text-xs text-muted-foreground">
                  attempt {execution.attempt} · {execution.status}
                </span>
              </span>
              <span className="ml-6 mt-1 flex flex-wrap gap-x-2 gap-y-1 text-xs text-muted-foreground">
                {execution.model && <span>model: {execution.model}</span>}
                {execution.duration_ms != null && (
                  <span>· {execution.duration_ms}ms</span>
                )}
                {execution.cost && (
                  <span>· {formatUsdCost(execution.cost)}</span>
                )}
                {execution.output_confidence && (
                  <span>· conf {execution.output_confidence}</span>
                )}
              </span>
            </button>
          </li>
        ))}
      </ol>
      <StageArtifactsDialog
        execution={selectedExecution}
        onOpenChange={(open) => {
          if (!open) setSelectedExecution(null);
        }}
      />
    </>
  );
}

/**
 * Exported for the park-limit notice: its "View last proposal"
 * opens the last `enrich` execution's artifacts directly, rather than making
 * the curator find that row in the timeline below.
 */
export function StageArtifactsDialog({
  execution,
  onOpenChange,
}: {
  execution: StageExecutionRead | null;
  onOpenChange: (open: boolean) => void;
}) {
  const repo = useItemsRepository();
  const [selectedRef, setSelectedRef] = React.useState<string | null>(null);
  const artifactsQuery = useQuery({
    queryKey: [
      "tina",
      "items",
      execution?.event_id,
      "stages",
      execution?.id,
      "artifacts",
    ],
    queryFn: () => repo.getStageArtifacts(execution!.event_id, execution!.id),
    enabled: Boolean(execution),
  });
  const data = artifactsQuery.data;
  const selectedArtifact =
    data?.artifacts.find((artifact) => artifact.ref === selectedRef) ??
    data?.artifacts[0] ??
    null;

  React.useEffect(() => {
    setSelectedRef(null);
  }, [execution?.id]);

  return (
    <Dialog open={Boolean(execution)} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] max-w-5xl gap-0 overflow-hidden p-0">
        <DialogHeader className="border-b px-5 py-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <DialogTitle className="flex items-center gap-2 text-base">
                <FileJson className="h-4 w-4" />
                {execution?.stage ?? "Stage"} artifacts
              </DialogTitle>
              <DialogDescription className="mt-1 font-mono text-xs">
                {execution
                  ? `attempt ${execution.attempt} · ${execution.id}`
                  : ""}
              </DialogDescription>
            </div>
            {execution && <Badge variant="secondary">{execution.status}</Badge>}
          </div>
        </DialogHeader>
        {artifactsQuery.isLoading ? (
          <div className="flex h-[520px] max-h-[calc(90vh-88px)] items-center justify-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" />
            Loading artifacts
          </div>
        ) : artifactsQuery.isError ? (
          <div className="p-5 text-sm text-red-600">
            {artifactErrorMessage(artifactsQuery.error)}
          </div>
        ) : data ? (
          <ArtifactsBrowser
            data={data}
            selectedArtifact={selectedArtifact}
            selectedRef={selectedArtifact?.ref ?? null}
            onSelect={setSelectedRef}
          />
        ) : null}
      </DialogContent>
    </Dialog>
  );
}

function ArtifactsBrowser({
  data,
  selectedArtifact,
  selectedRef,
  onSelect,
}: {
  data: StageArtifactsRead;
  selectedArtifact: StageArtifactRead | null;
  selectedRef: string | null;
  onSelect: (ref: string) => void;
}) {
  return (
    <div className="grid h-[560px] max-h-[calc(90vh-88px)] grid-cols-[260px_minmax(0,1fr)] overflow-hidden">
      <aside className="flex min-h-0 flex-col border-r bg-muted/20">
        <div className="border-b px-3 py-2">
          <p className="truncate font-mono text-[11px] text-muted-foreground">
            {data.prefix}
          </p>
        </div>
        {data.list_error && (
          <div className="border-b bg-amber-500/10 px-3 py-2 text-xs text-amber-800 dark:text-amber-200">
            {data.list_error}
          </div>
        )}
        <ScrollArea className="min-h-0 flex-1">
          <div className="flex flex-col gap-1 p-2">
            {data.artifacts.length === 0 ? (
              <p className="px-2 py-3 text-xs text-muted-foreground">
                No artifacts found for this stage.
              </p>
            ) : (
              data.artifacts.map((artifact) => (
                <button
                  key={artifact.ref}
                  type="button"
                  onClick={() => onSelect(artifact.ref)}
                  className={cn(
                    "flex flex-col gap-1 rounded-md px-2 py-2 text-left text-xs hover:bg-accent focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    selectedRef === artifact.ref && "bg-accent",
                  )}
                >
                  <span className="flex items-center justify-between gap-2">
                    <span className="truncate font-medium">
                      {artifact.name}
                    </span>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {artifact.kind}
                    </Badge>
                  </span>
                  <span className="text-muted-foreground">
                    {artifact.format} · {formatBytes(artifact.size_bytes)}
                  </span>
                </button>
              ))
            )}
          </div>
        </ScrollArea>
      </aside>
      <section className="flex min-h-0 min-w-0 flex-col overflow-hidden">
        {selectedArtifact ? (
          <>
            <div className="flex items-start justify-between gap-3 border-b px-4 py-3">
              <div className="min-w-0">
                <h3 className="truncate text-sm font-semibold">
                  {selectedArtifact.name}
                </h3>
                <p className="truncate font-mono text-[11px] text-muted-foreground">
                  {selectedArtifact.ref}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <CopyButton
                  text={selectedArtifact.ref}
                  message="GCS ref copied"
                />
                <CopyArtifactContentButton artifact={selectedArtifact} />
              </div>
            </div>
            <div className="flex items-center gap-2 border-b px-4 py-2 text-xs text-muted-foreground">
              <Badge variant="secondary">{selectedArtifact.format}</Badge>
              <span>
                {selectedArtifact.content_type ?? "unknown content type"}
              </span>
              <Separator orientation="vertical" className="h-3" />
              <span>{formatBytes(selectedArtifact.size_bytes)}</span>
              {selectedArtifact.truncated && (
                <Badge variant="outline">truncated</Badge>
              )}
            </div>
            <div className="min-h-0 flex-1 overflow-auto">
              <pre className="min-w-full w-max whitespace-pre p-4 font-mono text-xs leading-relaxed">
                {artifactPreview(selectedArtifact)}
              </pre>
            </div>
          </>
        ) : (
          <div className="flex h-full items-center justify-center text-sm text-muted-foreground">
            Select an artifact.
          </div>
        )}
      </section>
    </div>
  );
}

function CopyArtifactContentButton({
  artifact,
}: {
  artifact: StageArtifactRead;
}) {
  const content = artifactPreview(artifact);
  if (!content || artifact.format === "binary") return null;
  return (
    <Button
      type="button"
      size="sm"
      variant="outline"
      className="h-8 px-2 text-xs"
      onClick={() => navigator.clipboard.writeText(content)}
    >
      Copy content
    </Button>
  );
}

function artifactPreview(artifact: StageArtifactRead): string {
  if (artifact.error) return artifact.error;
  if (artifact.format === "json")
    return JSON.stringify(artifact.content, null, 2);
  if (artifact.format === "text") return String(artifact.content ?? "");
  if (artifact.format === "binary")
    return "Binary artifact preview is unavailable.";
  return "No preview available.";
}

function artifactErrorMessage(error: unknown): string {
  if (error instanceof Error) return error.message;
  return "Failed to load stage artifacts.";
}

function formatBytes(value: number | null): string {
  if (value == null) return "unknown size";
  if (value < 1024) return `${value} B`;
  const kib = value / 1024;
  if (kib < 1024) return `${kib.toFixed(1)} KiB`;
  return `${(kib / 1024).toFixed(1)} MiB`;
}
